import React, { useState } from 'react'
import PropTypes from 'prop-types'
import { useDispatch } from 'react-redux'
import { useFeatures } from 'flagged'
import { styled, useTheme } from '@mui/material/styles'
import {
  Backdrop,
  Box,
  Button,
  CircularProgress,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Modal,
  Typography,
} from '../../../../components/mui/Core/index'
import { fetchAllUsers } from '../../user-management-slice'
import { adminDisableUser } from '../../../../api/userauthentication/user-management'
import { deactivateUser } from '../../../../api/user/deactivate-user'
import { setSnackbar } from '../../../../components/GenericSnackbar/generic-snackbar-slice'
import Constants from '../../../../constants/en.json'
import Ellipse from '../../../../components/genericcomponents/Ellipse/ellipse'
import SubHeader from '../../../../components/SubHeader/sub-header'

const CenteredContainer = styled(Box)(() => ({
  display: 'flex',
  flexDirection: 'column',
  minHeight: '100vh',
  justifyContent: 'center',
  alignItems: 'center',
}))

function DeactivateUserModal({ isOpen, handleCloseModal, selectedUsers }) {
  const dispatch = useDispatch()
  const [isDeactivatingUser, setIsDeactivatingUser] = useState(false)
  const theme = useTheme()
  const features = useFeatures()
  const isUserServiceEnabled = features.isUserServiceEnabled

  const handleCloseAction = (reason) => {
    if (reason === 'backdropClick') {
      return
    }
    handleCloseModal()
  }

  const handleCancelModal = () => {
    handleCloseAction()
  }

  const handleDeactivateUser = async () => {
    /* When user service is disabled, disable users through Amplify */
    setIsDeactivatingUser(true)
    if (!isUserServiceEnabled) {
      const promises = []
      /* Building promises */
      selectedUsers.forEach((userName) => {
        if (!isUserServiceEnabled) {
          promises.push(
            adminDisableUser(userName).catch(() => {
              /* Nothing to do here */
            }),
          )
        }
      })

      /* Calling Promises */
      await Promise.all([promises])
        .then(() => onDeactivateUserSuccess())
        .catch(() => onDeactivateUserError())
    }

    /* When user service is enabled, disable users through microservice */
    if (isUserServiceEnabled) {
      const request = { userNames: selectedUsers }
      /* TODO: Create deactivate user slice to manage
               state when deprecating Amplify Auth */
      deactivateUser(request)
        .then(() => onDeactivateUserSuccess())
        .catch((exception) => onDeactivateUserError(exception))
    }
  }

  function onDeactivateUserSuccess() {
    setIsDeactivatingUser(false)
    handleCloseAction()
    dispatch(fetchAllUsers({ isUserServiceEnabled }))
    const deactivateUserMessage =
      selectedUsers.length > 1
        ? `${selectedUsers.length} ${Constants.userManagement.deactivateUser.USERS_DEACTIVATED}.`
        : `${selectedUsers[0]} ${Constants.userManagement.deactivateUser.USER_DEACTIVATED}.`
    dispatch(setSnackbar({ message: deactivateUserMessage, severity: 'success', action: true }))
  }

  function onDeactivateUserError(exception) {
    setIsDeactivatingUser(false)
    dispatch(setSnackbar({ message: exception?.message, severity: 'error', action: true }))
  }

  const deactivateStyle = {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: '496px',
    bgcolor: theme.palette.common.white,
    boxShadow: 24,
    padding: '10px 20px 10px 25px',
    border: 'none !important',
    borderRadius: '12px',
  }

  const cardBodyStyle = {
    paddingTop: '15px',
    color: theme.palette.common.black,
    fontFamily: 'AtlasGroteskRegular',
    fontSize: '16px',
    lineHeight: '20px',
  }

  const userListStyle = {
    paddingTop: '5px',
    '& li': {
      paddingBottom: '0px',
      paddingTop: '0px',
      '& .MuiListItemIcon-root': {
        alignSelf: 'flex-start',
        minWidth: '15px',
        paddingTop: '12px',
      },
      '& .MuiListItemText-root': {
        marginTop: '5px',
        marginBottom: '10px',
      },
    },
  }

  const listTextStyle = {
    color: theme.palette.common.black,
    fontFamily: 'AtlasGroteskRegular',
    fontSize: '16px',
    lineHeight: '20px',
  }

  const cancelButtonStyle = { mt: 5, mr: 2 }

  const deactivateButtonStyle = { mt: 5, mr: 1 }

  const loadingStyle = { borderRadius: '12px' }

  return (
    <Modal disableEscapeKeyDown open={isOpen} onClose={handleCloseAction}>
      <CenteredContainer>
        <Backdrop open={isDeactivatingUser} sx={loadingStyle}>
          <CircularProgress color='inherit' />
        </Backdrop>
        <Box sx={deactivateStyle}>
          <SubHeader title={Constants.userManagement.deactivateUser.DEACTIVATE_USERS} />
          <Typography sx={cardBodyStyle}>
            {Constants.userManagement.deactivateUser.ARE_YOU_SURE_MESSAGE}
          </Typography>
          <List sx={userListStyle}>
            {selectedUsers.map((user) => {
              return (
                <ListItem disablePadding key={user}>
                  <ListItemIcon>
                    <Ellipse />
                  </ListItemIcon>
                  <ListItemText primaryTypographyProps={listTextStyle} primary={user} />
                </ListItem>
              )
            })}
          </List>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'row',
              justifyContent: 'flex-end',
              marginBottom: '10px',
            }}
          >
            <Button
              onClick={handleCancelModal}
              variant='outlined'
              color='secondary'
              sx={cancelButtonStyle}
            >
              {Constants.userManagement.deactivateUser.CANCEL_BUTTON}
            </Button>
            <Button
              onClick={handleDeactivateUser}
              sx={deactivateButtonStyle}
              color='error'
              variant='contained'
              disabled={isDeactivatingUser}
            >
              {Constants.userManagement.deactivateUser.DEACTIVATE_BUTTON}
            </Button>
          </Box>
        </Box>
      </CenteredContainer>
    </Modal>
  )
}

DeactivateUserModal.propTypes = {
  // eslint-disable-next-line react/forbid-prop-types
  selectedUsers: PropTypes.array.isRequired,
  isOpen: PropTypes.bool,
  handleCloseModal: PropTypes.func,
}

export default DeactivateUserModal
